import { useRef, useState } from 'react';
import { toPng } from 'html-to-image';
import { Copy, Download, X, Image as ImageIcon, FileText, Check, Braces, Table } from 'lucide-react';
import { CardImage } from '@/components/CardImage';
import { InkDot } from '@/components/InkDot';
import { exportDeckAsText, exportDeckAsJson, exportDeckAsCsv } from '@/lib/deck-utils';
import type { DeckCard } from '@/hooks/useDecks';
import type { InkColor } from '@/lib/ink-utils';
import { toast } from 'sonner';



interface DeckExportSheetProps {
  open: boolean;
  onClose: () => void;
  deckName: string;
  ink1: InkColor;
  ink2: InkColor;
  cards: DeckCard[];
}

export function DeckExportSheet({ open, onClose, deckName, ink1, ink2, cards }: DeckExportSheetProps) {
  const [tab, setTab] = useState<'text' | 'json' | 'csv' | 'image'>('text');
  const [copied, setCopied] = useState(false);
  const [generating, setGenerating] = useState(false);
  const previewRef = useRef<HTMLDivElement>(null);

  if (!open) return null;

  const textBody = exportDeckAsText(deckName, cards);
  const jsonBody = exportDeckAsJson(deckName, [ink1, ink2], cards);
  const csvBody = exportDeckAsCsv(cards);
  const total = cards.reduce((s, c) => s + c.quantity, 0);

  const slug = deckName.replace(/[^a-z0-9]+/gi, '-').toLowerCase() || 'deck';

  const handleCopy = async (body: string, label: string) => {
    try {
      await navigator.clipboard.writeText(body);
      setCopied(true);
      toast.success(`${label} copied`);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      toast.error('Could not copy');
    }
  };

  const handleDownloadFile = (body: string, ext: string, mime: string) => {
    const blob = new Blob([body], { type: mime });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${slug}.${ext}`;
    a.click();
    URL.revokeObjectURL(url);
    toast.success(`Downloaded ${slug}.${ext}`);
  };

  const handleDownloadImage = async () => {
    if (!previewRef.current) return;
    setGenerating(true);
    try {
      const dataUrl = await toPng(previewRef.current, {
        cacheBust: true,
        pixelRatio: 2,
        backgroundColor: '#0a0b1a',
      });
      const a = document.createElement('a');
      a.href = dataUrl;
      a.download = `${slug}.png`;
      a.click();
      toast.success('Image downloaded');
    } catch (err) {
      toast.error('Could not generate image');
    } finally {
      setGenerating(false);
    }
  };

  // Sort cards by ink then cost for the visual grid
  const sorted = [...cards].sort((a, b) => {
    const ai = a.card?.ink_color || '';
    const bi = b.card?.ink_color || '';
    if (ai !== bi) return ai.localeCompare(bi);
    return (a.card?.cost ?? 0) - (b.card?.cost ?? 0);
  });

  const TABS: { id: typeof tab; label: string; icon: typeof FileText }[] = [
    { id: 'text', label: 'Text', icon: FileText },
    { id: 'json', label: 'JSON', icon: Braces },
    { id: 'csv', label: 'CSV', icon: Table },
    { id: 'image', label: 'Image', icon: ImageIcon },
  ];

  return (
    <div className="fixed inset-0 z-[60] flex items-end justify-center bg-background/80 backdrop-blur-sm" onClick={onClose}>
      <div className="animate-slide-up w-full max-w-[430px] max-h-[90vh] flex flex-col rounded-t-2xl border border-border bg-card" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between p-4 border-b border-border">
          <h3 className="font-display text-base font-semibold text-foreground">Export Deck</h3>
          <button onClick={onClose} className="text-muted-foreground"><X className="h-5 w-5" /></button>
        </div>

        <div className="flex gap-1 rounded-xl bg-secondary p-1 m-3">
          {TABS.map(t => {
            const Icon = t.icon;
            return (
              <button
                key={t.id}
                onClick={() => setTab(t.id)}
                className={`flex-1 flex items-center justify-center gap-1 rounded-lg py-2 text-[11px] font-medium font-body transition-colors ${tab === t.id ? 'bg-card text-foreground shadow-sm' : 'text-muted-foreground'}`}
              >
                <Icon className="h-3 w-3" /> {t.label}
              </button>
            );
          })}
        </div>

        <div className="flex-1 overflow-y-auto px-3 pb-3">
          {tab === 'text' && (
            <>
              <pre className="rounded-lg bg-secondary p-3 text-[11px] text-foreground font-mono whitespace-pre-wrap break-words max-h-[50vh] overflow-y-auto">{textBody}</pre>
              <div className="mt-3 grid grid-cols-2 gap-2">
                <button onClick={() => handleCopy(textBody, 'Deck list')} className="flex items-center justify-center gap-2 rounded-lg bg-primary py-3 text-sm font-medium text-primary-foreground font-body">
                  {copied ? <><Check className="h-4 w-4" /> Copied</> : <><Copy className="h-4 w-4" /> Copy</>}
                </button>
                <button onClick={() => handleDownloadFile(textBody, 'txt', 'text/plain')} className="flex items-center justify-center gap-2 rounded-lg bg-secondary py-3 text-sm font-medium text-foreground font-body">
                  <Download className="h-4 w-4" /> .txt
                </button>
              </div>
              <p className="mt-2 text-[10px] text-center text-muted-foreground font-body">
                Compatible with Dreamborn, Inkdecks, and most paste-in importers.
              </p>
            </>
          )}

          {tab === 'json' && (
            <>
              <pre className="rounded-lg bg-secondary p-3 text-[11px] text-foreground font-mono whitespace-pre-wrap break-words max-h-[50vh] overflow-y-auto">{jsonBody}</pre>
              <div className="mt-3 grid grid-cols-2 gap-2">
                <button onClick={() => handleCopy(jsonBody, 'JSON')} className="flex items-center justify-center gap-2 rounded-lg bg-primary py-3 text-sm font-medium text-primary-foreground font-body">
                  <Copy className="h-4 w-4" /> Copy
                </button>
                <button onClick={() => handleDownloadFile(jsonBody, 'json', 'application/json')} className="flex items-center justify-center gap-2 rounded-lg bg-secondary py-3 text-sm font-medium text-foreground font-body">
                  <Download className="h-4 w-4" /> .json
                </button>
              </div>
            </>
          )}

          {tab === 'csv' && (
            <>
              <pre className="rounded-lg bg-secondary p-3 text-[11px] text-foreground font-mono whitespace-pre-wrap break-words max-h-[50vh] overflow-y-auto">{csvBody}</pre>
              <div className="mt-3 grid grid-cols-2 gap-2">
                <button onClick={() => handleCopy(csvBody, 'CSV')} className="flex items-center justify-center gap-2 rounded-lg bg-primary py-3 text-sm font-medium text-primary-foreground font-body">
                  <Copy className="h-4 w-4" /> Copy
                </button>
                <button onClick={() => handleDownloadFile(csvBody, 'csv', 'text/csv')} className="flex items-center justify-center gap-2 rounded-lg bg-secondary py-3 text-sm font-medium text-foreground font-body">
                  <Download className="h-4 w-4" /> .csv
                </button>
              </div>
            </>
          )}

          {tab === 'image' && (

            <>
              <div className="rounded-lg overflow-hidden border border-border">
                {/* Off-screen-friendly capture target — rendered visibly so user can preview */}
                <div ref={previewRef} className="bg-background p-4 w-[400px] mx-auto">
                  <div className="flex items-center gap-2 mb-3">
                    <InkDot ink={ink1} size="md" />
                    <InkDot ink={ink2} size="md" />
                    <h4 className="font-display text-base font-semibold text-foreground flex-1 truncate">{deckName}</h4>
                    <span className="text-[10px] text-muted-foreground font-body">{total}/60</span>
                  </div>
                  <div className="grid grid-cols-6 gap-1">
                    {sorted.map(dc => (
                      <div key={dc.id} className="relative">
                        <CardImage
                          src={dc.card?.image_url ?? null}
                          alt={dc.card?.name ?? ''}
                          inkColor={dc.card?.ink_color as InkColor}
                          className="w-full aspect-[2/3] rounded-sm"
                        />
                        {dc.quantity > 1 && (
                          <span className="absolute -top-1 -right-1 flex h-4 w-4 items-center justify-center rounded-full bg-primary text-[8px] font-bold text-primary-foreground">
                            {dc.quantity}
                          </span>
                        )}
                      </div>
                    ))}
                  </div>
                  <p className="mt-3 text-[9px] text-center text-muted-foreground font-body">Built on LoreScout · lorescout.com</p>
                </div>
              </div>
              <button onClick={handleDownloadImage} disabled={generating} className="mt-3 w-full flex items-center justify-center gap-2 rounded-lg bg-primary py-3 text-sm font-medium text-primary-foreground font-body disabled:opacity-50">
                <Download className="h-4 w-4" /> {generating ? 'Generating...' : 'Download PNG'}
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
